import type { FileFormatScope } from '@steerfi/shared';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FileFormatApiRow } from '@/api/file-formats';
import {
  AddFileFormatModal,
  type LockedScope,
} from '@/components/file-formats/add-file-format-modal';
import { DeleteFileFormatDialog } from '@/components/file-formats/delete-file-format-dialog';
import { EditFileFormatModal } from '@/components/file-formats/edit-file-format-modal';
import { FileFormatsTable } from '@/components/file-formats/file-formats-table';
import { RestoreFileFormatDialog } from '@/components/file-formats/restore-file-format-dialog';
import {
  type FileFormatScopeFilter,
  type FileFormatStatusFilterUi,
  useFileFormats,
} from '@/hooks/use-file-formats';

const HIDDEN_COLUMNS: ReadonlySet<string> = new Set(['scope']);

export interface PartyFileFormatsPanelProps {
  partyId: string;
  scope: FileFormatScope;
  partyName?: string | undefined;
  partyCode?: string | undefined;
  hideHeaderAdd?: boolean | undefined;
  /** When provided with `onAddOpenChange`, the parent owns the add-modal open state. */
  addOpen?: boolean | undefined;
  onAddOpenChange?: ((open: boolean) => void) | undefined;
}

export const PartyFileFormatsPanel: React.FC<PartyFileFormatsPanelProps> = ({
  partyId,
  scope,
  partyName,
  partyCode,
  hideHeaderAdd,
  addOpen: controlledAddOpen,
  onAddOpenChange,
}) => {
  const {
    tableData,
    isLoading,
    error,
    filters,
    scopeUi,
    statusUi,
    setSearch,
    setScope,
    setStatus,
    setPage,
    setSort,
    refetch,
  } = useFileFormats({ partyId, scope });

  const [internalAddOpen, setInternalAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<FileFormatApiRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<FileFormatApiRow | null>(null);
  const [restoreRow, setRestoreRow] = useState<FileFormatApiRow | null>(null);

  const addOpen = controlledAddOpen ?? internalAddOpen;
  const setAddOpen = onAddOpenChange ?? setInternalAddOpen;

  const lockedScope: LockedScope = useMemo(
    () => ({ scope, partyId, partyName, partyCode }),
    [scope, partyId, partyName, partyCode]
  );

  const handleFilterChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      const raw = Array.isArray(value) ? (value[0] ?? '') : value;
      if (filterKey === 'scope') {
        setScope((raw === '' ? 'all' : raw) as FileFormatScopeFilter);
      } else if (filterKey === 'status') {
        setStatus(raw as FileFormatStatusFilterUi);
      }
    },
    [setScope, setStatus]
  );

  const currentFilterValues = useMemo<Record<string, string>>(
    () => ({
      scope: scopeUi === 'all' ? '' : scopeUi,
      status: statusUi,
    }),
    [scopeUi, statusUi]
  );

  const openAdd = useCallback(() => setAddOpen(true), [setAddOpen]);

  return (
    <>
      <FileFormatsTable
        data={tableData}
        isLoading={isLoading}
        error={error}
        currentFilterValues={currentFilterValues}
        currentSortKey={filters.sortBy ?? 'format_name'}
        currentSortOrder={filters.order ?? 'asc'}
        searchValue={filters.search ?? ''}
        onFilterChange={handleFilterChange}
        onSortChange={setSort}
        onPageChange={setPage}
        onSearchChange={setSearch}
        onAdd={openAdd}
        onEdit={setEditRow}
        onDelete={setDeleteRow}
        onRestore={setRestoreRow}
        hiddenColumns={HIDDEN_COLUMNS}
        {...(hideHeaderAdd !== undefined ? { hideHeaderAdd } : {})}
      />

      <AddFileFormatModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={() => {
          setAddOpen(false);
          refetch();
        }}
        lockedScope={lockedScope}
      />

      <EditFileFormatModal
        open={editRow !== null}
        row={editRow}
        onOpenChange={(open) => {
          if (!open) setEditRow(null);
        }}
        onSuccess={() => {
          setEditRow(null);
          refetch();
        }}
      />

      <DeleteFileFormatDialog
        open={deleteRow !== null}
        row={deleteRow}
        onOpenChange={(open) => {
          if (!open) setDeleteRow(null);
        }}
        onSuccess={() => {
          setDeleteRow(null);
          refetch();
        }}
      />

      <RestoreFileFormatDialog
        open={restoreRow !== null}
        row={restoreRow}
        onOpenChange={(open) => {
          if (!open) setRestoreRow(null);
        }}
        onSuccess={() => {
          setRestoreRow(null);
          refetch();
        }}
      />
    </>
  );
};
